import { AccessLevel } from './user.interface';

/**
 * Capabilities do sistema. Cada item de menu/rota declara UMA permission;
 * a política (quais perfis têm cada capability) vive só neste arquivo.
 */
export enum Permission {
  HOME = 'HOME',
  CADASTROS_BASICOS = 'CADASTROS_BASICOS',
  CADASTROS_TRANSPORTE = 'CADASTROS_TRANSPORTE',
  CALC_COMBINACOES = 'CALC_COMBINACOES',
  CALC_AVANCADOS = 'CALC_AVANCADOS',
  CALC_ESTUFAMENTO = 'CALC_ESTUFAMENTO',
  ARQ_INSTRUCOES = 'ARQ_INSTRUCOES',
  // Execução (antiga ARQ_MONTAGEM, dividida por módulo comercial):
  // - EXEC_REMESSA: módulo Remessas e Picking (Execução de Remessa).
  // - EXEC_CARGA: módulo Estufamento de Carga (Execução de Carga).
  EXEC_REMESSA = 'EXEC_REMESSA',
  EXEC_CARGA = 'EXEC_CARGA',
  RELATORIOS = 'RELATORIOS',
  GUIAS = 'GUIAS',
  EDITAR_EMPRESA = 'EDITAR_EMPRESA',
  ADMIN_EMPRESAS = 'ADMIN_EMPRESAS',
}

const { ADMIN_SUPER, OTIMIZACAO, PACKING, CARGO_STUFFING, OPERATOR } = AccessLevel;

/**
 * Matriz recurso × perfil (espelha os 3 módulos comerciais):
 * - admin_super: MK Digital, acessa tudo + administra empresas.
 * - otimizacao: Pacote Otimizações (todos os módulos do cliente).
 * - packing: Desenvolvimento e Simulação (Buscar Combinações + Instruções) + cadastros básicos.
 * - cargo_stuffing: módulo Estufamento de Carga (Planejamento + Execução de Carga).
 * - operator: Remessas e Picking > Execução de Remessa.
 */
export const PERMISSION_ROLES: Record<Permission, AccessLevel[]> = {
  [Permission.HOME]: [ADMIN_SUPER, OTIMIZACAO, PACKING],
  [Permission.CADASTROS_BASICOS]: [ADMIN_SUPER, OTIMIZACAO, PACKING],
  [Permission.CADASTROS_TRANSPORTE]: [ADMIN_SUPER, OTIMIZACAO],
  [Permission.CALC_COMBINACOES]: [ADMIN_SUPER, OTIMIZACAO, PACKING],
  [Permission.CALC_AVANCADOS]: [ADMIN_SUPER, OTIMIZACAO],
  [Permission.CALC_ESTUFAMENTO]: [ADMIN_SUPER, OTIMIZACAO, CARGO_STUFFING],
  [Permission.ARQ_INSTRUCOES]: [ADMIN_SUPER, OTIMIZACAO, PACKING],
  // Execução de Remessa: fluxo de remessas (não inclui cargo_stuffing).
  [Permission.EXEC_REMESSA]: [ADMIN_SUPER, OTIMIZACAO, OPERATOR],
  // Execução de Carga: fluxo de estufamento (não inclui operator).
  [Permission.EXEC_CARGA]: [ADMIN_SUPER, OTIMIZACAO, CARGO_STUFFING],
  [Permission.RELATORIOS]: [ADMIN_SUPER, OTIMIZACAO],
  [Permission.GUIAS]: [ADMIN_SUPER, OTIMIZACAO, PACKING],
  [Permission.EDITAR_EMPRESA]: [ADMIN_SUPER, OTIMIZACAO, PACKING],
  [Permission.ADMIN_EMPRESAS]: [ADMIN_SUPER],
};

/**
 * Quais perfis cada ator pode criar/excluir (dropdown do cadastro de
 * usuários E regra do botão de excluir). admin_super nunca é gerenciável
 * pela aplicação. Espelhado no BE (roles.constants) — manter em sincronia.
 */
export const ROLE_MANAGEABLE_ROLES: Partial<Record<AccessLevel, AccessLevel[]>> = {
  [ADMIN_SUPER]: [OPERATOR, CARGO_STUFFING, PACKING, OTIMIZACAO],
  [OTIMIZACAO]: [OPERATOR, CARGO_STUFFING, PACKING, OTIMIZACAO],
  // packing não cria otimizacao (evita auto-upgrade do módulo contratado).
  [PACKING]: [OPERATOR, CARGO_STUFFING, PACKING],
};

/**
 * Representante comercial (flag can_grant_trial_access, independe do
 * perfil): ganha ADMIN > Empresas restrito às empresas que ele cadastrou e o
 * Como usar (onde fica o tutorial do representante).
 */
export const TRIAL_GRANTER_PERMISSIONS: Permission[] = [Permission.ADMIN_EMPRESAS, Permission.GUIAS];

/**
 * Perfis que o representante atribui nas empresas de teste que cadastrou.
 * Espelhado no BE (TRIAL_GRANTER_MANAGEABLE_ROLES) — manter em sincronia.
 */
export const TRIAL_GRANTER_MANAGEABLE_ROLES: AccessLevel[] = [OPERATOR, CARGO_STUFFING, PACKING, OTIMIZACAO];

/**
 * Ordem de exibição dos perfis: primeiro os planos comerciais (Packing →
 * Otimização, em ordem de upgrade), depois os perfis operacionais.
 */
export const ROLE_ORDER: AccessLevel[] = [PACKING, OTIMIZACAO, OPERATOR, CARGO_STUFFING, ADMIN_SUPER];

/** O que cada perfil libera (exibido no cadastro de usuários). */
export const ROLE_DESCRIPTIONS: Record<AccessLevel, string> = {
  [OPERATOR]: 'Acessa somente Remessas e Picking > Execução de Remessa.',
  [CARGO_STUFFING]: 'Acessa o módulo Estufamento de Carga (Planejamento e Execução de Carga).',
  [PACKING]: 'Desenvolvimento e Simulação (Buscar Combinações e Instruções de Montagem), Cadastros (Produtos, Kits, Caixas, Usuários) e Como usar.',
  [OTIMIZACAO]: 'Acesso completo: Cadastros, Desenvolvimento e Simulação, Remessas e Picking, Estufamento de Carga, Relatórios e Como usar.',
  [ADMIN_SUPER]: 'MK Digital: acesso total + administração de empresas e usuários.',
};
